/**
 * `ChatInfobar` — the ambient status strip at the bottom of the chat pane
 * (chat-infobar miniplan §3.1), in the VSCode status-bar register: one line,
 * tiny type, recessive inks, never competing with the conversation.
 *
 * ⚠ ALWAYS RENDERED, INCLUDING WHEN EMPTY — operator ruling 2026-07-30.
 * This REVERSES the plan's original §3.1 ("Idle = empty. No border, no
 * background, no reserved chrome — the bar *is* its message"). The bar is a
 * permanent fixture of the pane, so the composer never shifts when a message
 * arrives and the lawyer always knows where status appears. An idle bar shows
 * its chrome and no text; it never invents a resting message, because a bar
 * that says "tudo certo" when nothing is being watched would be asserting
 * something it does not know (§1.5 honesty rules still bind).
 *
 * Mounted through `ConversationShell`'s `footer` slot, so it is inside the
 * pane, below the composer, and width-coupled to the pane by construction.
 *
 * NO INTERACTION in v1 beyond a `title` tooltip carrying the full text:
 * clicking status text that does nothing teaches users the bar is dead.
 *
 * ── TWO REGIONS, because v2 is indicators (operator 2026-07-30) ─────────
 * A VSCode status bar is not a message line — it is a row of persistent
 * INDICATOR segments (branch, sync counts, problem counts, remote) with
 * transient text sharing the row. Lexiun has such indicators today living
 * elsewhere, notably the gateway/network connector in the left sidebar, and
 * they are migration candidates under the operator's "the infobar is the
 * only channel for this kind of messaging" ruling.
 *
 * So the bar is a CONTAINER OF REGIONS from the start, not a single line
 * that would have to be torn apart later. THREE regions (operator
 * 2026-07-30) — two fixed indicator blocks holding the outer edges, with the
 * rotating message area between them:
 *
 *   [ indicators LEFT ][ messages — transient, rotating, truncates ][ indicators RIGHT ]
 *
 * The indicator blocks are the bar's stable landmarks: they keep their
 * positions so a lawyer learns where to look, while the middle is the only
 * part that changes. Both blocks are `shrink-0` and the middle is the only
 * `flex-1 min-w-0` — so when text is long it TRUNCATES rather than pushing
 * an indicator off the edge. A half-rendered status is worse than a
 * shortened sentence.
 *
 * Both blocks now carry real segments (the workspace connector on the left,
 * the agent/task counts on the right). There is still deliberately NO
 * indicator REGISTRY — two segments is not enough to design a contract
 * against, so they are mounted directly and the registry waits until a third
 * proves the shape.
 *
 * ── THE v2 INDICATOR CONTRACT, as the operator specified it ─────────────
 * Named segments (operator 2026-07-30): the background AGENT/TASK COUNTS
 * (today the "Agentes 362 / Tarefas 321" tab counters) and the WORKSPACE
 * CONNECTOR relocated out of the left sidebar's `SidebarUtility`.
 *
 * Each indicator is PERSISTENT, carries a TOOLTIP, and IS A BUTTON — the
 * VSCode model, where a segment both reports and acts (click the branch,
 * get the checkout menu).
 *
 * That does NOT contradict §3.1's "no click actions in v1", which is about
 * the MESSAGES region and stays: transient status text that does nothing
 * when clicked teaches users the bar is dead. An indicator is the opposite
 * case — it is a standing affordance, so being inert is what would surprise.
 * Two regions, two interaction models, on purpose.
 *
 * ⚠ ONE BLOCKER v2 MUST RESOLVE FIRST, for the connector specifically.
 * This bar lives in `ConversationShell`, whose only consumers are
 * `CaseWorkspaceLayout` and `GlobalConversationLayout` — i.e. it exists on
 * CHAT SURFACES ONLY. `SidebarUtility` lives in `ActivityBar`, which is
 * shell-level and therefore present everywhere. Relocating the connector
 * as-is would DELETE it from `/home` and every non-chat route (Contatos,
 * Pendências, Agendamentos, Organização, Configurações) — the surfaces where
 * a lawyer is least likely to notice a dead gateway until it costs them
 * something. Relocation needs an answer to "where does the connector live
 * off a chat surface", not just a move.
 *
 * The counts have no such problem: their stores are hydrated app-wide
 * regardless of the sidebar (the detail dialogs poll unconditionally from
 * `App.tsx`), so a counts segment is honest wherever the bar renders.
 */
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, Info } from "lucide-react";
import { useInfobarStore, type InfobarMode } from "@/stores/useInfobarStore";
import {
  AgendamentosIndicator,
  AgentCountsIndicator,
  GatewayIndicator,
  PendenciasIndicator,
} from "./ChatInfobarIndicators";
import { cn } from "@/lib/utils";

/**
 * ONE INK FOR EVERY MODE (operator 2026-07-31): messages carry no colour at
 * all. Colour and weight are the two loudest levers there are, and a status
 * line that reaches for either is competing with the conversation above it.
 *
 * Mode is still legible — it rides the ICON (✓ / ⚠ / spinner / ⓘ), which
 * distinguishes without shouting. That is the same division the rest of this
 * bar uses: glyphs carry category, type stays uniform and quiet.
 */
const MESSAGE_INK = "text-coffee-600 dark:text-cream-400";

/** 13px — paired to the 12px type so the glyph reads as its equal, not as a
 *  speck beside it. `shrink-0` so the icon survives truncation of the text. */
function ModeIcon({ mode }: { mode: InfobarMode }) {
  // PROGRESS HAS NO GLYPH (operator 2026-07-31, trial): a spinner is a motion
  // source in a strip whose whole job is to stay out of the way, and it reads
  // as "the app is busy" rather than "this step is under way". The trailing
  // ellipsis below carries the same meaning without moving.
  if (mode === "progress") return null;
  if (mode === "success") return <Check size={13} aria-hidden className="shrink-0" />;
  if (mode === "error") return <AlertTriangle size={13} aria-hidden className="shrink-0" />;
  return <Info size={13} aria-hidden className="shrink-0" />;
}

export function ChatInfobar() {
  const { t } = useTranslation();
  const current = useInfobarStore((s) => s.current);
  const mode: InfobarMode = current?.mode ?? "info";

  return (
    <div
      data-testid="chat-infobar"
      data-mode={current ? mode : undefined}
      data-key={current?.key}
      data-sticky={current?.sticky ? "true" : undefined}
      data-empty={current ? undefined : "true"}
      // `min-h-`, NEVER `h-` — a fixed height is how a 33px line ends up
      // clipped inside a 24px box. 24px seats 12px type + a 13px glyph with
      // `leading-[1]`; the `min-h-` means the strip still GROWS rather than
      // clipping if either ever does, which is the whole reason the rule
      // exists. Tightened from 26px (operator 2026-07-31) — the glyph, not
      // the line box, is what sets the real floor here.
      //
      // ⚠ `text-[12px]` with an explicit `leading-` on purpose:
      // `tailwind-merge` treats an unrecognised `text-*` as a COLOUR, so a
      // custom size token and a `text-<colour>` in one `cn()` silently drop
      // one of the two — no error, the class is simply absent from the DOM.
      className={cn(
        "flex min-h-[24px] w-full shrink-0 items-center gap-3 overflow-hidden px-3",
        // REVERSE BAR, matching the case-summary strip at the top of the pane
        // (operator 2026-07-30): a border alone left the strip effectively
        // invisible — an always-present bar that cannot be SEEN is not a
        // fixture, it is dead space. The dark ground gives it an identity and
        // bookends the conversation between two pieces of chrome of the same
        // family.
        //
        // ⚠ Was "dark in BOTH themes" until 2026-09-07 (operator): a dark bar on
        // the cream canvas read as dark-theme chrome leaking into light mode,
        // so light mode now gets a LIGHT ground of the same family and every
        // ink in this bar carries a light/dark fork. The reverse-bar identity
        // survives per theme — the bar is still one step off its canvas — it
        // just no longer inverts the theme. Dark values are unchanged.
        "bg-cream-100 text-coffee-700 dark:bg-coffee-800 dark:text-cream-100",
        // THIN, never bold (operator 2026-07-30). Weight is the loudest
        // typographic lever there is; a status bar that reaches for it is
        // competing with the conversation it sits under. Size carries
        // legibility here, and weight stays out of it — `font-light`
        // cascades so no segment can quietly bold itself back up.
        "text-[12px] font-extralight leading-[1]",
      )}
      // Ambient, not urgent: `polite` lets a screen reader finish what it is
      // saying first. `assertive` on a murmur would be hostile — it would
      // interrupt the assistant's own reply being read out.
      aria-live="polite"
      aria-atomic="true"
      aria-label={t("infobar.ariaLabel")}
      title={current?.text}
    >
      {/* ── FIXED indicators, LEFT block ────────────────────────────────
          EMPTY IN v1 BY DESIGN: `empty:hidden` means it renders nothing and
          reserves nothing (not even its gap) until a segment exists, so it
          costs a lawyer no pixels today. */}
      <div
        data-testid="chat-infobar-indicators-left"
        className="flex shrink-0 items-center gap-1 empty:hidden"
      >
        <GatewayIndicator />
      </div>

      {/* ── TRANSIENT messages, the MIDDLE ──────────────────────────────
          One line at a time, rotating as the queue drains. The ONLY
          flexible region, so it is the only thing that truncates. */}
      <div
        data-testid="chat-infobar-messages"
        className={cn("flex min-w-0 flex-1 items-center gap-1.5", MESSAGE_INK)}
      >
        {current && (
          <>
            <ModeIcon mode={mode} />
            <span className="truncate">{current.text}</span>
            {/* ANIMATED DOTS — the in-progress marker. Rides OUTSIDE the
                truncating span and is `shrink-0`, so a long line loses its
                middle rather than its dots; otherwise the one thing saying
                "still running" would be the first casualty of truncation.
                The row gap is 6px, which read as a detached glyph; `-ml-0.5`
                trims it to ~4px — one space at this size (operator
                2026-07-31), so the dots follow the sentence rather than
                either hugging it or floating off it.
                Suppressed when the text already ends in an ellipsis so
                nothing renders "…...". */}
            {mode === "progress" && !/[…]$|\.\.\.$/.test(current.text) && (
              <span aria-hidden className="-ml-0.5 inline-flex shrink-0">
                {[0, 200, 400].map((d) => (
                  <span
                    key={d}
                    className="animate-lex-dot"
                    style={{ animationDelay: `${d}ms` }}
                  >
                    .
                  </span>
                ))}
              </span>
            )}
          </>
        )}
      </div>

      {/* ── FIXED indicators, RIGHT block ───────────────────────────────
          Same contract as the left block; which segments land on which side
          is v2's call, when there are real segments to place. */}
      {/* Blocks are SEPARATED, not merely adjacent (operator 2026-07-31):
          `divide-x` plus real padding gives each segment its own cell, so a
          row of icon+number pairs reads as distinct facts instead of one
          run-on string of digits. The dividers are barely-there on purpose —
          enough to group, not enough to draw the eye. */}
      <div
        data-testid="chat-infobar-indicators-right"
        className="flex shrink-0 items-center divide-x divide-cream-100/10 empty:hidden"
      >
        <div className="px-1">
          <PendenciasIndicator />
        </div>
        <div className="px-1">
          <AgendamentosIndicator />
        </div>
        {/* Agents/tasks stay RIGHTMOST (operator 2026-07-31) — they are the
            most volatile numbers on the bar, so they sit at the edge where a
            change cannot shove its neighbours sideways. */}
        <div className="px-1">
          <AgentCountsIndicator />
        </div>
      </div>
    </div>
  );
}
